import { DOCUMENT, Injectable, inject, signal } from "@angular/core";
import { MessageService } from "primeng/api";
import { LoggingService, LogLevel } from "./logging.service";

/**
 * sessionStorage key holding the timestamp of the last recovery reload.
 *
 * Also hardcoded (it must be, see below) as GUARD_KEY in the inline bootstrap
 * guard in index.html, which covers the entry-bundle failures that happen
 * before this service exists to run (a failed main-<hash>.js/
 * polyfills-<hash>.js/scripts-<hash>.js load means Angular never bootstraps).
 * That script cannot import this constant -- it runs before any bundler
 * output exists -- so the two copies must be kept in sync by hand. A spec at
 * src/index.bootstrap-guard.spec.ts pins them against drifting.
 */
export const CHUNK_RELOAD_GUARD_KEY = "lc.chunkReloadAt";

/**
 * How long a recovery reload is remembered. A second chunk failure inside this
 * window means the reload did not help, so we stop and show the error card.
 * A failure after it is treated as a fresh deployment and heals again.
 *
 * Also hardcoded as GUARD_WINDOW_MS in the inline bootstrap guard in
 * index.html -- see the comment on CHUNK_RELOAD_GUARD_KEY above.
 */
export const CHUNK_RELOAD_GUARD_WINDOW_MS = 60_000;

/** Long enough for the toast to be read before the page goes away. */
export const CHUNK_RELOAD_DELAY_MS = 1500;

/**
 * Lower-cased fragments identifying a failed dynamic import. Chromium, Firefox
 * and Safari each word it differently, and the webpack-era ChunkLoadError name
 * still appears in wrapped errors.
 *
 * Deliberately broad: the guard caps the cost of a false positive at one
 * reload, whereas a false negative leaves the user on a page that cannot work.
 */
const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "chunkloaderror",
];

/**
 * Recovers from a lazy chunk that cannot be loaded because the replica that
 * served index.html is not the replica that served the chunk (issue #366).
 *
 * Angular emits content-hashed chunks and index.html references the hashes from
 * its own build, while the gateway load-balances every request independently
 * across ui replicas. During a rolling deployment those two can disagree, and
 * no health check can express "same build as the one that served index.html".
 * A tab opened before a deployment holds its old index.html indefinitely, so
 * this is also the only cure for the stale-tab case.
 *
 * Sibling of BackendHealthService, which applies the same detect/notify/recover
 * shape to backend outages via the __reload__ detour route.
 */
@Injectable({
  providedIn: "root",
})
export class ChunkLoadRecoveryService {
  private document = inject(DOCUMENT);
  private messageService = inject(MessageService);
  private loggingService = inject(LoggingService);

  /**
   * Set when a reload has already been tried and the chunk still failed.
   * Read by MainComponent to render the error card instead of looping.
   */
  readonly unrecoverable = signal<string | null>(null);

  /**
   * True between scheduling a reload and the reload actually firing.
   * Deliberately an instance field, not sessionStorage: it must NOT survive
   * the reload. Two entry points (the global ErrorHandler and the router's
   * withNavigationErrorHandler) can both see the same chunk failure
   * microseconds apart; without this, the second call would find the guard
   * already fresh and set `unrecoverable`, flashing the error card for the
   * CHUNK_RELOAD_DELAY_MS before the already-scheduled reload fires. It is
   * cleared the moment the reload actually fires (see the setTimeout below)
   * and, in production, that moment is moot anyway - location.reload() tears
   * this instance down regardless. After a real reload, the fresh instance
   * starts with this false, so a still-fresh sessionStorage guard is
   * correctly treated as "the reload did not help" and does set
   * `unrecoverable`.
   */
  private reloadScheduled = false;

  private get window(): Window | null {
    return this.document.defaultView;
  }

  /**
   * Clears a previously shown error card. A completed navigation proves the
   * app can load chunks again (a failed chunk load never produces a
   * NavigationEnd), so MainComponent calls this on every NavigationEnd
   * rather than leaving the card up until a manual full page reload.
   */
  dismiss(): void {
    this.unrecoverable.set(null);
  }

  isChunkLoadError(error: unknown): boolean {
    const message = this.messageOf(error).toLowerCase();
    if (!message) {
      return false;
    }
    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
  }

  /**
   * @returns true if this service took ownership of the error, in which case
   *          the caller must not report it further.
   */
  handle(error: unknown): boolean {
    if (!this.isChunkLoadError(error)) {
      return false;
    }

    const message = this.messageOf(error);
    const view = this.window;
    if (!view) {
      // Server-side or a detached document: nothing to reload.
      return false;
    }

    this.loggingService.log(
      "ChunkLoadRecoveryService",
      `Chunk load failure: ${message}`,
      LogLevel.WARN
    );

    if (this.reloadAlreadyAttempted(view)) {
      if (this.reloadScheduled) {
        // A reload from an earlier call on this same page load is already
        // in flight (e.g. the ErrorHandler and the router's navigation
        // error handler both saw this failure) - recovery is under way,
        // not exhausted, so don't flash the error card.
        return true;
      }
      // Deliberately do NOT clear the guard here: a route navigation can
      // fire several dynamic-import failures at once (route chunk, child
      // chunks, router preloading), and clearing on the first repeat would
      // let a burst re-arm a second real reload. The guard is only allowed
      // to lapse by expiring after CHUNK_RELOAD_GUARD_WINDOW_MS, which is
      // what lets a later, genuinely new deployment heal.
      this.unrecoverable.set(message);
      return true;
    }

    try {
      view.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      // sessionStorage is blocked entirely (Safari "block all cookies", some
      // embedded contexts): the guard can't be persisted, so a second
      // failure after this reload would look "fresh" and could reload again
      // in a loop. Degrade the same way as a detected repeat failure below:
      // show the error card instead of reloading blind.
      this.unrecoverable.set(message);
      return true;
    }
    this.messageService.add({
      key: "messages",
      severity: "info",
      summary: "New version deployed",
      detail: "A new version of LipidCompass was deployed — reloading…",
      life: CHUNK_RELOAD_DELAY_MS,
    });
    this.reloadScheduled = true;
    view.setTimeout(() => {
      // Reset just before the reload actually fires. In production this is
      // moot - location.reload() tears down this instance a moment later -
      // but it keeps a same-instance test double honest: once a reload has
      // actually been triggered, a further failure seeing a still-fresh
      // guard is "the reload did not help" and must set `unrecoverable`,
      // exactly as it would on the fresh instance a real reload produces.
      this.reloadScheduled = false;
      // location.reload() keeps the current URL, so the user lands back
      // where they were rather than on the home page.
      view.location.reload();
    }, CHUNK_RELOAD_DELAY_MS);
    return true;
  }

  private reloadAlreadyAttempted(view: Window): boolean {
    let raw: string | null;
    try {
      raw = view.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    } catch {
      // sessionStorage access is blocked entirely (Safari "block all
      // cookies", some embedded contexts). We cannot tell whether a reload
      // was already tried, and reloading blind risks a loop if the chunk
      // failure recurs on every load. Fail safe by treating storage being
      // unreadable the same as "yes, already attempted": handle() falls
      // into the branch below and shows the error card instead of reloading.
      return true;
    }
    if (raw === null) {
      return false;
    }
    const previous = Number(raw);
    if (!Number.isFinite(previous)) {
      return false;
    }
    return Date.now() - previous < CHUNK_RELOAD_GUARD_WINDOW_MS;
  }

  private messageOf(error: unknown): string {
    if (typeof error === "string") {
      return error;
    }
    if (error instanceof Error) {
      return `${error.name}: ${error.message}`;
    }
    if (error && typeof error === "object" && "message" in error) {
      return String((error as { message: unknown }).message);
    }
    return "";
  }
}
